import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@resume-studio/ui";
import * as React from "react";

import { CampaignAudience, CampaignOfferType, PlanType } from "@/admin/services";

const slugify = (input: string): string =>
  input
    .toLowerCase()
    .trim()
    .replace(/[^\d a-z-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export interface CampaignFormState {
  slug: string;
  name: string;
  description: string;
  offerType: CampaignOfferType;
  grantedPlan: PlanType;
  grantedDurationDays: number;
  startsAt: string;
  endsAt: string;
  targetAudience: CampaignAudience;
  maxRedemptions: number | null;
  bannerText: string;
  pageHeadline: string;
  pageSubheadline: string;
  discountPercent: number;
  discountDurationMonths: number;
  // Optional override — when blank, server mints a coupon with matching
  // percent + duration. Lets marketing reuse an externally-created coupon.
  stripeCouponId: string;
  enableStripeCouponOverride: boolean;
}

export const today = () => new Date().toISOString().slice(0, 10);
export const inDays = (n: number) => {
  const date = new Date(Date.now() + n * 24 * 60 * 60 * 1000);
  return date.toISOString().slice(0, 10);
};

export const initialCampaignFormState: CampaignFormState = {
  slug: "",
  name: "",
  description: "",
  offerType: "STRIPE_DISCOUNT",
  grantedPlan: "pro",
  grantedDurationDays: 14,
  startsAt: today(),
  endsAt: inDays(14),
  targetAudience: "NEW_USERS",
  maxRedemptions: 1000,
  bannerText: "",
  pageHeadline: "",
  pageSubheadline: "",
  discountPercent: 50,
  discountDurationMonths: 1,
  stripeCouponId: "",
  enableStripeCouponOverride: false,
};

interface CampaignFormProps {
  value: CampaignFormState;
  onChange: React.Dispatch<React.SetStateAction<CampaignFormState>>;
  onSubmit: (event: React.FormEvent) => void;
  onCancel: () => void;
  /**
   * Edit mode locks the slug + offer type because changing them mid-run breaks
   * the public `/offer/:slug` link and silently misrepresents what Stripe will
   * actually charge. Discount-shape edits (percent / duration) are likewise
   * server-rejected — see `AdminCampaignsService.update`.
   */
  mode: "create" | "edit";
  submitLabel: string;
  isSubmitting?: boolean;
}

export const CampaignForm = ({
  value: form,
  onChange: setForm,
  onSubmit,
  onCancel,
  mode,
  submitLabel,
  isSubmitting = false,
}: CampaignFormProps) => {
  const isEdit = mode === "edit";

  const set = <K extends keyof CampaignFormState>(key: K, value: CampaignFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleNameChange = (value: string) => {
    set("name", value);
    if (!isEdit && (!form.slug || form.slug === slugify(form.name))) {
      set("slug", slugify(value));
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:gap-6 lg:max-w-3xl">
      <Section title="Basics">
        <Field label="Name">
          <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)} />
        </Field>
        <Field
          label="Slug"
          hint={
            isEdit
              ? "Locked after creation — the public /offer/:slug link is part of the campaign's identity."
              : "Used in /offer/:slug. Auto-derived from name; edit to override."
          }
        >
          <Input
            value={form.slug}
            onChange={(e) => set("slug", slugify(e.target.value))}
            disabled={isEdit}
          />
        </Field>
        <Field label="Description">
          <Textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            rows={2}
          />
        </Field>
      </Section>

      <Section title="Offer">
        <Field label="Type" hint={isEdit ? "Locked after creation." : undefined}>
          <Select
            value={form.offerType}
            onValueChange={(value) => set("offerType", value as CampaignOfferType)}
            disabled={isEdit}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="STRIPE_DISCOUNT">Stripe discount</SelectItem>
              <SelectItem value="FREE_ACCESS">Free access</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        {form.offerType === "STRIPE_DISCOUNT" ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label="Discount %"
                hint={isEdit ? "Locked — change requires a new campaign." : undefined}
              >
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={form.discountPercent}
                  onChange={(e) => set("discountPercent", Number(e.target.value))}
                  disabled={isEdit}
                />
              </Field>
              <Field
                label="Duration (months)"
                hint={isEdit ? "Locked — change requires a new campaign." : undefined}
              >
                <Input
                  type="number"
                  min={1}
                  value={form.discountDurationMonths}
                  onChange={(e) => set("discountDurationMonths", Number(e.target.value))}
                  disabled={isEdit}
                />
              </Field>
            </div>
            <Field
              label={isEdit ? "Stripe coupon ID (override)" : "Reuse existing Stripe coupon?"}
              hint={
                isEdit
                  ? "Swap the coupon if Stripe's misconfigured. Leave blank to keep the current one."
                  : undefined
              }
            >
              {!isEdit && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.enableStripeCouponOverride}
                    onCheckedChange={(checked) =>
                      set("enableStripeCouponOverride", Boolean(checked))
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    Off creates a fresh coupon at save time.
                  </span>
                </div>
              )}
              {(isEdit || form.enableStripeCouponOverride) && (
                <Input
                  value={form.stripeCouponId}
                  onChange={(e) => set("stripeCouponId", e.target.value)}
                  placeholder="coupon_…"
                  className={isEdit ? "" : "mt-2"}
                />
              )}
            </Field>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Granted plan" hint={isEdit ? "Locked after creation." : undefined}>
              <Select
                value={form.grantedPlan}
                onValueChange={(value) => set("grantedPlan", value as PlanType)}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="starter_pass">Starter Pass</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Duration (days)">
              <Input
                type="number"
                min={1}
                value={form.grantedDurationDays}
                onChange={(e) => set("grantedDurationDays", Number(e.target.value))}
              />
            </Field>
          </div>
        )}
      </Section>

      <Section title="Window & audience">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Starts">
            <Input
              type="date"
              value={form.startsAt}
              onChange={(e) => set("startsAt", e.target.value)}
            />
          </Field>
          <Field label="Ends">
            <Input
              type="date"
              value={form.endsAt}
              onChange={(e) => set("endsAt", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Audience">
          <Select
            value={form.targetAudience}
            onValueChange={(value) => set("targetAudience", value as CampaignAudience)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NEW_USERS">New users</SelectItem>
              <SelectItem value="FREE_USERS">Free users</SelectItem>
              <SelectItem value="EVERYONE">Everyone</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Cap (max redemptions)" hint="Leave blank for uncapped.">
          <Input
            type="number"
            min={0}
            value={form.maxRedemptions ?? ""}
            onChange={(e) =>
              set("maxRedemptions", e.target.value === "" ? null : Number(e.target.value))
            }
          />
        </Field>
      </Section>

      <Section title="Copy">
        <Field label="Banner text" hint="Shown in the dashboard banner. Plain text only.">
          <Input value={form.bannerText} onChange={(e) => set("bannerText", e.target.value)} />
        </Field>
        <Field label="Page headline">
          <Input value={form.pageHeadline} onChange={(e) => set("pageHeadline", e.target.value)} />
        </Field>
        <Field label="Page subheadline">
          <Textarea
            value={form.pageSubheadline}
            onChange={(e) => set("pageSubheadline", e.target.value)}
            rows={2}
          />
        </Field>
      </Section>

      <div className="sticky bottom-0 -mx-4 flex flex-col-reverse items-stretch gap-2 border-t border-border bg-background/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:flex-row sm:items-center sm:justify-end sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-md border border-border bg-card">
    <header className="border-b border-border px-4 py-3 sm:px-5">
      <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
    </header>
    <div className="space-y-4 p-4 sm:p-5">{children}</div>
  </section>
);

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="grid gap-1.5">
    <Label className="text-[12px] font-medium text-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[12px] text-muted-foreground">{hint}</p>}
  </div>
);
